import { useState } from 'react';
import type { ArchiveResultDto, DrawingDto, RemoveDrawingResultDto } from '@shared/types';
import { localDateTime, money, titleCase } from '@shared/format';
import { errorMessage, useApp, useLoad } from '../context';
import { navigate } from '../router';
import { Card, Empty, ErrorBox, Loading, Modal, Pill, TopBar } from '../components/ui';

const KINDS: Record<string, 'ok' | 'warn' | 'bad' | 'info' | 'muted'> = { Draft: 'muted', Staged: 'warn', Started: 'info', Completed: 'ok', Cancelled: 'bad' };
/** Completed and cancelled drawings can be archived; drafts are cancelled first. Staged/spinning ones are on the TV. */
const canArchive = (d: DrawingDto) => d.status === 'Completed' || d.status === 'Cancelled' || d.status === 'Draft';
/** Trash can: anything not on the TV right now. */
const canRemove = canArchive;
const REASONS = ['Test spin', 'Set up by mistake', 'Wrong prize or entrants'];

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
    </svg>
  );
}

export function DrawingsList() {
  const { api, toast } = useApp();
  const [view, setView] = useState<'active' | 'archived'>('active');
  const { data, error, loading, reload } = useLoad(
    () => api.get<DrawingDto[]>(`/api/drawings?take=200${view === 'archived' ? '&archived=true' : ''}`), [api, view]);
  const [confirm, setConfirm] = useState<null | { ids: string[]; drafts: number; label: string }>(null);
  const [busy, setBusy] = useState(false);
  const [trash, setTrash] = useState<DrawingDto | null>(null);
  const [reason, setReason] = useState(REASONS[0]!);
  const askRemove = (d: DrawingDto) => { setReason(REASONS[0]!); setTrash(d); };
  const doRemove = async () => {
    if (!trash) return;
    setBusy(true);
    try {
      const r = await api.post<RemoveDrawingResultDto>(`/api/drawings/${trash.drawingId}/remove`, { reason });
      toast(`${r.reference} deleted`);
      setTrash(null); reload();
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };

  const open = (d: DrawingDto) => navigate(d.status === 'Started' || d.status === 'Completed' ? `/drawings/${d.drawingId}/live` : `/drawings/${d.drawingId}`);
  const archivable = (data ?? []).filter((d) => d.status === 'Completed' || d.status === 'Cancelled');

  const askArchive = (list: DrawingDto[], label: string) =>
    setConfirm({ ids: list.map((d) => d.drawingId), drafts: list.filter((d) => d.status === 'Draft').length, label });
  const doArchive = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const r = await api.post<ArchiveResultDto>('/api/drawings/archive', { drawingIds: confirm.ids });
      toast(`${r.archived} drawing${r.archived === 1 ? '' : 's'} archived`);
      setConfirm(null); reload();
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const restore = async (d: DrawingDto) => {
    try { await api.post(`/api/drawings/${d.drawingId}/unarchive`); toast(`${d.reference} restored`); reload(); }
    catch (e) { toast(errorMessage(e), 'error'); }
  };

  return (
    <div className="page">
      <TopBar title="Drawings" />
      <div className="quick-grid">
        {['weekly', 'monthly', 'quarterly', 'annual'].map((t) => (
          <button key={t} className="btn btn-primary" onClick={() => navigate(`/draw/${t}`)}>New {titleCase(t)}</button>
        ))}
      </div>
      <div className="seg-row seg-row-2" role="tablist" aria-label="Drawings view">
        <button role="tab" aria-selected={view === 'active'} className={`seg ${view === 'active' ? 'on' : ''}`} onClick={() => setView('active')}>Current</button>
        <button role="tab" aria-selected={view === 'archived'} className={`seg ${view === 'archived' ? 'on' : ''}`} onClick={() => setView('archived')}>Archived</button>
      </div>
      {view === 'active' && archivable.length > 0 && (
        <button className="btn btn-secondary btn-block" onClick={() => askArchive(archivable, `all ${archivable.length} completed and cancelled drawings`)}>
          Archive all past drawings ({archivable.length})
        </button>
      )}
      {loading && !data && <Loading />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <Card>
          {data.length === 0 ? <Empty>{view === 'archived' ? 'No archived drawings.' : 'No current drawings. Past drawings you archived are under Archived.'}</Empty> : data.map((d) => (
            <div key={d.drawingId} className="list-row list-row-split">
              <button className="list-row-main" onClick={() => open(d)}>
                <div>
                  <strong>{titleCase(d.drawingType)}</strong> <Pill kind={KINDS[d.status] ?? 'muted'}>{d.status}</Pill>
                  <div className="muted small">{d.reference} • {d.prizeName}{d.prizeValue > 0 ? ` (${money(d.prizeValue)})` : ''}</div>
                  <div className="muted small">{d.winnerName ? `Winner: ${d.winnerName} • ` : ''}{localDateTime(d.completedAtUtc ?? d.createdAtUtc)}</div>
                  {view === 'archived' && d.archivedAtUtc && <div className="muted small">Archived {localDateTime(d.archivedAtUtc)}{d.archivedBy ? ` by ${d.archivedBy}` : ''}</div>}
                </div>
              </button>
              {view === 'active' && canArchive(d) && (
                <button className="btn btn-secondary btn-sm" onClick={() => askArchive([d], d.reference)}>Archive</button>
              )}
              {view === 'archived' && <button className="btn btn-secondary btn-sm" onClick={() => void restore(d)}>Restore</button>}
              {canRemove(d) && (
                <button className="btn btn-danger-outline btn-sm btn-icon" onClick={() => askRemove(d)}
                  aria-label={`Delete ${d.reference}`} title="Delete (test spin or mistake)"><TrashIcon /></button>
              )}
            </div>
          ))}
        </Card>
      )}
      {trash && (
        <Modal title="Delete this drawing?" onClose={() => setTrash(null)} actions={<>
          <button className="btn btn-secondary" onClick={() => setTrash(null)}>Keep</button>
          <button className="btn btn-danger" disabled={busy || reason.trim().length < 3} onClick={doRemove}>{busy ? 'Deleting…' : 'Delete'}</button>
        </>}>
          <p><strong>{titleCase(trash.drawingType)}</strong> • {trash.reference}{trash.winnerName ? <> • Winner: <strong>{trash.winnerName}</strong></> : null}</p>
          <p className="muted small">Only for test spins and mistakes. It comes off the drawings list, the winner report and CSV, and the TV's Recent Winner. {trash.winnerName ? `${trash.winnerName} won't sit out the next drawing because of it. ` : ''}Points aren't touched, and the audit log keeps a record of who deleted it and why.</p>
          <div className="chips mt" role="radiogroup" aria-label="Reason">
            {REASONS.map((r) => (
              <button key={r} role="radio" aria-checked={reason === r} className={`chip ${reason === r ? 'on' : ''}`} onClick={() => setReason(r)}>{r}</button>
            ))}
          </div>
          <label className="field mt"><span>Reason</span>
            <input className="input" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} /></label>
          {trash.status === 'Draft' && <p className="warn small">This draft hasn't been run, so deleting also cancels it.</p>}
        </Modal>
      )}
      {confirm && (
        <Modal title="Archive drawings?" onClose={() => setConfirm(null)} actions={<>
          <button className="btn btn-secondary" onClick={() => setConfirm(null)}>Keep</button>
          <button className="btn btn-primary" disabled={busy} onClick={doArchive}>{busy ? 'Archiving…' : 'Archive'}</button>
        </>}>
          <p>Archive {confirm.label}?</p>
          <p className="muted small">This only hides {confirm.ids.length === 1 ? 'it' : 'them'} from this list. Winners, the winner report and the audit log keep everything, and you can restore {confirm.ids.length === 1 ? 'it' : 'them'} from Archived.</p>
          {confirm.drafts > 0 && <p className="warn small">This draft hasn't been run, so archiving also cancels it.</p>}
        </Modal>
      )}
    </div>
  );
}
